/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Config-driven list page.
 *
 * Two data shapes:
 * - `pagination: "client"` (default): the endpoint returns an array and the
 *   existing DataTable pages, searches and exports it in the browser. This is
 *   what the Travel trip-entity lists use.
 * - `pagination: "server"`: the endpoint returns {items,total,page,page_size}
 *   and accepts page/page_size/search/sort_by/sort_order plus the config's
 *   filters. This is what the Real Estate lists use.
 *
 * Config keys: title, subtitle, addLabel, listApiPath | fetchList(axios,params),
 * pagination, detailRoute (string base or fn(row)), primaryField,
 * columns(navigate, helpers), objectType, supportsDelete, deleteApiPath,
 * rowActions(row, ctx), renderAddDialog({open,onClose,onCreated}) |
 * createForm {apiPath, fields, title} | addPath, filters, urlParams,
 * searchPlaceholder, defaultSort {by, order}, openCreated, editForm {fields},
 * pageTitle (UI v2: a page header with this title and the record count
 * replaces the title strip inside the card)
 * (adds an "Edit" row action that edits in a dialog, for records with no
 * detail page).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { Alert, Card, Grid, Icon, IconButton, ListItemIcon, Menu, MenuItem, Snackbar } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";
import { useAuth } from "auth/Auth";
import { usePermission } from "auth/usePermission";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import PearlLogoLoader from "components/PearlLogoLoader";
import PageHeader from "components/v2/PageHeader";
import useDebounce from "hooks/useDebounce";
import ServerDataTable from "./ServerDataTable";
import EntityFormDialog from "./EntityFormDialog";
import { apiErrorMessage, normalizeListResponse } from "./listResponse";

export function resolveDetailPath(config, row) {
  if (!row?.id || !config) return null;
  if (typeof config.detailRoute === "function") return config.detailRoute(row);
  const base = config.detailRoute || config.route;
  return base ? `${base}/${row.id}` : null;
}

function ActionMenu({ row, onView, extraActions, onDelete }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const close = () => setAnchorEl(null);
  const hasAny = Boolean(onView || onDelete || extraActions.length);
  if (!hasAny) return null;
  return (
    <>
      <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={close}>
        {onView && (
          <MenuItem onClick={() => { onView(row); close(); }}>
            <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
            View
          </MenuItem>
        )}
        {extraActions.map((action) => (
          <MenuItem key={action.label} onClick={() => { action.onClick(row); close(); }}>
            {action.icon ? <ListItemIcon>{action.icon}</ListItemIcon> : null}
            {action.label}
          </MenuItem>
        ))}
        {onDelete && (
          <MenuItem onClick={() => { onDelete(row.id); close(); }} sx={{ color: "error.main" }}>
            <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: "error.main" }} /></ListItemIcon>
            Delete
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

ActionMenu.propTypes = {
  row: PropTypes.object.isRequired,
  onView: PropTypes.func,
  extraActions: PropTypes.arrayOf(PropTypes.object),
  onDelete: PropTypes.func,
};

ActionMenu.defaultProps = { onView: null, extraActions: [], onDelete: null };

const EntityList = ({ config, permissionObject }) => {
  // The object whose grant governs the page. Resolved per route (the Travel
  // wrapper derives it from the entity key, the RE configs name it), so the
  // name is dynamic here; usePermissionCoverage.test.js checks the configs.
  const { canCreate, canEdit, canDelete } = usePermission(permissionObject);
  const { axiosInstance, isAuthReady, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const serverMode = config?.pagination === "server";
  const v2Header = useTheme()?.pearlUi === "v2" && Boolean(config?.pageTitle);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(config?.pageSize || 25);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 350);
  const [filterValues, setFilterValues] = useState({});
  const [sort, setSort] = useState({
    by: config?.defaultSort?.by || null,
    order: config?.defaultSort?.order || "asc",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });
  const [editingRow, setEditingRow] = useState(null);

  // Filters carried in the URL (?project_id=12) are passed straight through.
  const urlParams = useMemo(() => {
    const out = {};
    const qs = new URLSearchParams(location.search);
    (config?.urlParams || []).forEach((key) => {
      const v = qs.get(key);
      if (v !== null && v !== "") out[key] = v;
    });
    return out;
  }, [location.search, config]);

  const fetchRows = useCallback(async () => {
    if (!config || !axiosInstance || !isAuthReady || !isAuthenticated) return;
    setIsLoading(true);
    try {
      const params = serverMode
        ? {
            page,
            page_size: pageSize,
            ...(debouncedSearch ? { search: debouncedSearch } : {}),
            ...(sort.by ? { sort_by: sort.by, sort_order: sort.order } : {}),
            ...Object.fromEntries(Object.entries(filterValues).filter(([, v]) => v !== "" && v != null)),
            ...(config.baseParams || {}),
            ...urlParams,
          }
        : { ...(config.baseParams || {}), ...urlParams };
      const response = config.fetchList
        ? await config.fetchList(axiosInstance, params)
        : await axiosInstance.get(config.listApiPath, Object.keys(params).length ? { params } : undefined);
      const normalized = normalizeListResponse(response?.data);
      setRows(normalized.items);
      setTotal(normalized.total);
    } catch (error) {
      console.error(`Error fetching ${config.title}:`, error);
      setRows([]);
      setTotal(0);
      setSnackbar({ open: true, message: `Error loading ${config.title}`, severity: "error" });
    } finally {
      setIsLoading(false);
    }
  }, [config, axiosInstance, isAuthReady, isAuthenticated, serverMode, page, pageSize, debouncedSearch, sort, filterValues, urlParams]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);


  const handleViewDetail = useCallback(
    (rowData) => {
      const path = resolveDetailPath(config, rowData);
      if (!path) return;
      navigate(path, { state: { name: rowData?.[config.primaryField] } });
    },
    [config, navigate]
  );

  const deleteApiPath = config?.deleteApiPath || config?.detailApiPath || config?.apiPath;
  const allowDelete = Boolean(config?.supportsDelete && canDelete && deleteApiPath);
  const handleDeleteRow = useCallback((id) => setDeleteConfirm({ open: true, id }), []);

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.id || !deleteApiPath) return;
    try {
      await axiosInstance.delete(`${deleteApiPath}/${deleteConfirm.id}`);
      setSnackbar({ open: true, message: "Record deleted successfully", severity: "success" });
      await fetchRows();
    } catch (error) {
      console.error(`Error deleting ${config.title}:`, error);
      setSnackbar({ open: true, message: apiErrorMessage(error, "Failed to delete record"), severity: "error" });
      throw error;
    }
  };

  const actionCtx = useMemo(
    () => ({ navigate, canEdit, canDelete, canCreate, refresh: fetchRows, axiosInstance, notify: (message, severity = "success") => setSnackbar({ open: true, message, severity }) }),
    [navigate, canEdit, canDelete, canCreate, fetchRows, axiosInstance]
  );

  const renderActionMenu = useCallback(
    (row) => (
      <ActionMenu
        row={row}
        onView={resolveDetailPath(config, row) ? handleViewDetail : null}
        extraActions={[
          ...(config?.editForm && canEdit
            ? [{ label: "Edit", icon: <Icon fontSize="small">edit</Icon>, onClick: (r) => setEditingRow(r) }]
            : []),
          ...(config?.rowActions ? config.rowActions(row, actionCtx) || [] : []),
        ]}
        onDelete={allowDelete ? handleDeleteRow : null}
      />
    ),
    [config, handleViewDetail, actionCtx, allowDelete, handleDeleteRow, canEdit]
  );

  const tableData = useMemo(() => {
    if (!config) return { columns: [], rows: [] };
    const baseColumns = config.columns(navigate, { renderActionMenu, ...actionCtx });
    const { primaryField } = config;
    let columns = [...baseColumns];

    if (primaryField) {
      const primaryIndex = columns.findIndex((col) => col.accessor === primaryField);
      if (primaryIndex > 0) {
        const [primaryCol] = columns.splice(primaryIndex, 1);
        columns.unshift(primaryCol);
      }
    }

    columns = columns.map((col) => {
      if (!primaryField || col.accessor !== primaryField) return col;
      const OriginalCell = col.Cell;
      return {
        ...col,
        Cell: (props) => {
          const content = OriginalCell ? <OriginalCell {...props} /> : (props.value ?? "-");
          return (
            <MDTypography
              component="span"
              variant="caption"
              color="info"
              fontWeight="medium"
              sx={{ cursor: "pointer" }}
              onClick={() => handleViewDetail(props.row.original)}
            >
              {content}
            </MDTypography>
          );
        },
      };
    });

    if (!columns.some((col) => col.accessor === "actions")) {
      columns.push({
        Header: "Actions",
        accessor: "actions",
        width: "8%",
        align: "center",
        disableSortBy: true,
        Cell: ({ row }) => renderActionMenu(row.original),
      });
    }

    return { columns, rows };
  }, [config, rows, navigate, renderActionMenu, actionCtx, handleViewDetail]);

  if (!config) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3} px={3}>
          <MDTypography variant="h6">Unknown list.</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  const hasAdd = Boolean(config.renderAddDialog || config.createForm || config.addPath);
  const handleAddClick = () => {
    if (config.addPath) {
      navigate(typeof config.addPath === "function" ? config.addPath(urlParams) : config.addPath);
      return;
    }
    setDialogOpen(true);
  };
  const handleCreated = (created) => {
    setSnackbar({ open: true, message: `${config.title} created`, severity: "success" });
    if (config.openCreated && created?.id) {
      const path = resolveDetailPath(config, created);
      if (path) {
        navigate(path);
        return;
      }
    }
    fetchRows();
  };

  const addButton = canCreate && hasAdd ? (
    <MDButton variant="gradient" color={v2Header ? "info" : "dark"} onClick={handleAddClick}>
      <Icon sx={{ fontWeight: "bold" }}>add</Icon>
      &nbsp;{config.addLabel || "Add"}
    </MDButton>
  ) : null;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {v2Header && (
        <MDBox pt={3}>
          <PageHeader
            title={config.pageTitle}
            summary={[
              isLoading ? null : `${total.toLocaleString()} ${(total === 1 ? config.title : config.pageTitle).toLowerCase()}`,
              config.subtitle,
            ]
              .filter(Boolean)
              .join(" · ")}
            actions={addButton}
          />
        </MDBox>
      )}
      <MDBox pt={v2Header ? 0 : 6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              {!v2Header && (
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1}
              >
                <MDBox>
                  <MDTypography variant="h6" color="white">
                    {config.title}
                  </MDTypography>
                  <MDTypography variant="caption" color="white" opacity={0.86}>
                    {config.subtitle}
                  </MDTypography>
                </MDBox>
                {addButton}
              </MDBox>
              )}
              <MDBox pt={v2Header ? 2 : 3}>
                {serverMode ? (
                  <ServerDataTable
                    columns={tableData.columns}
                    rows={rows}
                    total={total}
                    page={page}
                    pageSize={pageSize}
                    loading={isLoading}
                    search={search}
                    onSearchChange={
                      config.searchable === false
                        ? null
                        : (text) => {
                            setSearch(text);
                            setPage(1);
                          }
                    }
                    searchPlaceholder={config.searchPlaceholder || `Search ${config.title.toLowerCase()}...`}
                    filters={config.filters}
                    filterValues={filterValues}
                    onFilterChange={(param, value) => {
                      setFilterValues((prev) => ({ ...prev, [param]: value }));
                      setPage(1);
                    }}
                    sortBy={sort.by}
                    sortOrder={sort.order}
                    onSortChange={config.sortable === false ? null : (by, order) => setSort({ by, order })}
                    onPageChange={setPage}
                    onPageSizeChange={(size) => {
                      setPageSize(size);
                      setPage(1);
                    }}
                    onRowClick={config.rowClickOpensDetail ? handleViewDetail : null}
                  />
                ) : isLoading ? (
                  <MDBox py={6} display="flex" justifyContent="center">
                    <PearlLogoLoader size="md" message="Loading data..." />
                  </MDBox>
                ) : (
                  <DataTable
                    table={tableData}
                    isSorted
                    noEndBorder
                    canSearch
                    exportCSV
                    entriesPerPage={{
                      defaultValue: 10,
                      entries: [5, 10, 15, 20, 25],
                    }}
                    showTotalEntries
                    pagination={{ variant: "gradient", color: "info" }}
                    objectType={config.objectType}
                    onFilterClear={fetchRows}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {config.renderAddDialog
        ? config.renderAddDialog({ open: dialogOpen, onClose: () => setDialogOpen(false), onCreated: handleCreated })
        : null}
      {!config.renderAddDialog && config.createForm ? (
        <EntityFormDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          title={config.createForm.title || config.addLabel || `New ${config.title}`}
          apiPath={config.createForm.apiPath || config.apiPath || config.listApiPath}
          fields={config.createForm.fields}
          defaults={{ ...(config.createForm.defaults || {}), ...urlParams }}
          onCreated={handleCreated}
        />
      ) : null}

      {config.editForm ? (
        <EntityFormDialog
          open={Boolean(editingRow)}
          onClose={() => setEditingRow(null)}
          title={`Edit ${config.title}`}
          apiPath={config.editForm.apiPath || config.apiPath || config.listApiPath}
          fields={config.editForm.fields}
          record={editingRow}
          onCreated={() => {
            setSnackbar({ open: true, message: `${config.title} updated`, severity: "success" });
            fetchRows();
          }}
        />
      ) : null}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ConfirmDeleteModal
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, id: null })}
        onConfirm={handleDeleteConfirm}
        title={`Delete ${config.title}`}
        message={`Are you sure you want to delete this ${config.title.toLowerCase()}? This action cannot be undone.`}
      />
    </DashboardLayout>
  );
};

EntityList.propTypes = {
  config: PropTypes.object,
  permissionObject: PropTypes.string,
};

EntityList.defaultProps = {
  config: null,
  permissionObject: undefined,
};

export default EntityList;
